import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, IconButton } from '../../../components';
import { ApiError, apiErrorMessage, apiRequest } from '../../../lib/api';
import { useAuthStore } from '../../../lib/auth-store';
import type { ListResponse } from '../../../lib/useCrud';

type CommentRow = {
  id: string;
  body: string;
  created_at: string;
  user_id: string | null;
  author: { id: string; full_name: string; email: string } | null;
};

export function CommentsTab({ leadId }: { leadId: string }) {
  const qc = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const isAdmin = currentUser?.roles.includes('admin') ?? false;

  const comments = useQuery({
    queryKey: ['lead-comments', leadId],
    queryFn: () =>
      apiRequest<ListResponse<CommentRow>>(`/comments?lead_id=${leadId}&limit=100`),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['lead-comments', leadId] });

  const createMut = useMutation({
    mutationFn: (body: string) =>
      apiRequest<{ data: CommentRow }>('/comments', {
        method: 'POST',
        body: { lead_id: leadId, body },
      }),
    onSuccess: invalidate,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/comments/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
    onError: (err) => alert(apiErrorMessage(err, 'Delete failed')),
  });

  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!draft.trim()) return;
    setError(null);
    try {
      await createMut.mutateAsync(draft.trim());
      setDraft('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to post');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <form onSubmit={onSubmit} className="card">
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <textarea
            className="input"
            rows={3}
            placeholder="Add a comment…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          {error && <div className="alert">{error}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="primary"
              type="submit"
              disabled={createMut.isPending || !draft.trim()}
            >
              {createMut.isPending ? 'Posting…' : 'Post comment'}
            </Button>
          </div>
        </div>
      </form>

      {comments.isLoading && <div className="card card-body muted">Loading…</div>}
      {comments.error && (
        <div className="alert">
          {comments.error instanceof Error ? comments.error.message : 'Failed to load'}
        </div>
      )}
      {comments.data && comments.data.data.length === 0 && (
        <div className="card card-body muted">No comments yet.</div>
      )}
      {comments.data?.data.map((c) => {
        const canDelete = isAdmin || c.user_id === currentUser?.id;
        return (
          <div key={c.id} className="card">
            <div
              className="card-body"
              style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <strong>{c.author?.full_name ?? 'Unknown user'}</strong>
                <span className="muted" style={{ fontSize: 11 }}>
                  {new Date(c.created_at).toLocaleString()}
                </span>
                <div style={{ flex: 1 }} />
                {canDelete && (
                  <IconButton
                    name="trash"
                    label="Delete comment"
                    onClick={() => deleteMut.mutate(c.id)}
                  />
                )}
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{c.body}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
